import { API_STAT_UNITS } from '../../../utils/constants/api';
import { SORT_DIRECTIONS } from '../../../utils/constants/sortDirections';
import { trackObjectChangeEvent } from '../../../utils/helpers/snowplow';
import { SCOUT_TEMPLATE_MODES } from '../Scout.constants';
import {
  BIO_CATEGORY,
  DEFAULT_METRICS,
  SCOUT_PAGINATION_OPTIONS,
} from '../ScoutResults/ScoutPlayers/ScoutPlayers.constants';
import { DEFAULT_SCOUT_POSITION } from '../ScoutResults/ScoutResults.constants';
import { TOAST_MESSAGES } from '../ScoutResults/ScoutTemplateManagement/ScoutTemplateManagement.constants';

export const createScoutSlice = (set, get) => ({
  // --- State ---
  selectedStats: DEFAULT_METRICS,
  selectedPositions: [DEFAULT_SCOUT_POSITION],
  selectedBiometrics: [],
  showFavourites: false,
  activeTemplateId: null,
  templateName: '',
  savedScoutTemplates: [],
  loadModalOpen: false,
  statDefinitions: [],
  scoutFavourites: [],
  scoutTabIndex: 0,
  scoutPlayersPageSize: SCOUT_PAGINATION_OPTIONS[1].value, // default 25
  scoutPlayersActivePage: 1,
  scoutPlayersSortDirection: SORT_DIRECTIONS.ASCENDING,
  scoutPlayersSortKey: 'name',
  scoutPlayersCount: 0,
  scoutSelectedCategoryName: BIO_CATEGORY.name,
  scoutSelectedSubCategoryName: BIO_CATEGORY.subcategories[0].name,
  scoutStatCategories: [],
  scoutTableSearchText: '',
  // --- Actions ---
  setScoutStatCategories: (categories) =>
    set({ scoutStatCategories: categories }),
  setScoutTableSearchText: (text) => set({ scoutTableSearchText: text }),
  setScoutSelectedCategoryName: (name) =>
    set({ scoutSelectedCategoryName: name }),
  setScoutSelectedSubCategoryName: (name) =>
    set({ scoutSelectedSubCategoryName: name }),
  setScoutPlayersCount: (count) => set({ scoutPlayersCount: count }),
  handleScoutPlayersSort: (statName, headers) => {
    const { scoutPlayersSortKey, scoutPlayersSortDirection } = get();
    if (statName === scoutPlayersSortKey) {
      set({
        scoutPlayersSortDirection:
          scoutPlayersSortDirection === SORT_DIRECTIONS.ASCENDING
            ? SORT_DIRECTIONS.DESCENDING
            : SORT_DIRECTIONS.ASCENDING,
        scoutPlayersActivePage: 1,
      });
    } else {
      const sortColumn = headers.find((h) => h.key === statName);
      const defaultSortDirection =
        sortColumn?.units === API_STAT_UNITS.STRING
          ? SORT_DIRECTIONS.ASCENDING
          : SORT_DIRECTIONS.DESCENDING;
      set({
        scoutPlayersSortKey: statName,
        scoutPlayersSortDirection: defaultSortDirection,
        scoutPlayersActivePage: 1,
      });
    }
  },
  setScoutPlayersSortKey: (key) => set({ scoutPlayersSortKey: key }),
  setScoutPlayersSortDirection: (direction) =>
    set({ scoutPlayersSortDirection: direction }),
  setScoutPlayersActivePage: (page) => set({ scoutPlayersActivePage: page }),
  setScoutPlayersPageSize: (size) => {
    trackObjectChangeEvent('Widget', 'Edit', 'Scout Players Page Size');
    set({ scoutPlayersPageSize: size });
  },
  setScoutTabIndex: (index) => set({ scoutTabIndex: index }),
  updatePlayerMetric: (metricDefinitions) => {
    const state = get();
    const metricsToUpdate = Array.isArray(metricDefinitions)
      ? metricDefinitions
      : [metricDefinitions];

    const existingKeys = state.selectedStats.map((m) => m.key);
    const newMetrics = metricsToUpdate.filter(
      (metric) => !existingKeys.includes(metric.key),
    );

    const playsMetric = state.selectedStats.find(
      (metric) => metric.key === 'plays',
    );

    const metricsToUpdateKeys = metricsToUpdate.map((m) => m.key);
    const filteredSelectedStats = state.selectedStats.filter((m) =>
      metricsToUpdateKeys.includes(m.key),
    );

    set({
      selectedStats: [
        playsMetric,
        ...filteredSelectedStats,
        ...newMetrics,
      ].filter(Boolean),
    });
  },
  removeMetric: (metric) => {
    const state = get();
    const otherSelectedMetrics = state.selectedStats.filter(
      (s) => s.name !== metric.name,
    );
    set({ selectedStats: otherSelectedMetrics });
  },
  metricValueChanged: (metric, newValue) => {
    const state = get();
    const otherSelectedMetrics = state.selectedStats.filter(
      (s) => s.name !== metric.name,
    );
    if (metric.value !== newValue) {
      trackObjectChangeEvent('Widget', 'Edit', metric.name);
      const modifiedMetric = { ...metric, value: newValue };
      const newStatList = otherSelectedMetrics.concat(modifiedMetric);
      set({ selectedStats: newStatList });
    }
  },
  setScoutFavourites: (favourites) => {
    set({ scoutFavourites: favourites });
  },
  persistScoutFavourites: async () => {
    try {
      const { token, scoutFavourites } = get();
      if (!token) return;
      const apiUrl = process.env.ACCOUNT_CONFIG;
      // Fetch latest account config for ETag
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `bearer ${token}`,
        },
      });
      const data = await response.json();
      const etag = response.headers.get('ETag');
      // Merge new favourites
      const updatedAccount = { ...data, scoutFavourites };
      await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          Authorization: `bearer ${token}`,
          'If-Match': etag,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedAccount),
      });
    } catch (err) {
      console.error('Failed to add data with ETag:', err);
    }
  },
  setStatDefinitions: (defs) =>
    set({ statDefinitions: defs, videoModalOpen: false }),
  setSavedScoutTemplates: (templates) =>
    set({ savedScoutTemplates: templates, videoModalOpen: false }),
  setSelectedStats: (stats) => set({ selectedStats: stats }),
  setSelectedPositions: (positions) => set({ selectedPositions: positions }),
  setSelectedBiometrics: (biometrics) =>
    set({ selectedBiometrics: biometrics }),
  setShowFavourites: (show) => set({ showFavourites: show }),
  setActiveTemplateId: (id) => set({ activeTemplateId: id }),
  setTemplateName: (name) => set({ templateName: name }),
  setLoadModalOpen: (open) => set({ loadModalOpen: open }),
  clearUnsaved: () => {
    const state = get();
    const activeTemplate = state.savedScoutTemplates.find(
      (s) => s.id === state.activeTemplateId,
    );

    set({
      selectedStats: activeTemplate?.selectedStats || DEFAULT_METRICS,
      selectedPositions: activeTemplate?.selectedPositions || [
        DEFAULT_SCOUT_POSITION,
      ],
      selectedBiometrics: activeTemplate?.selectedBiometrics || [],
      showFavourites: activeTemplate?.showFavourites || false,
      templateName: activeTemplate?.name || '',
      toast: { title: '', message: '', type: 'success' },
      showToast: false,
    });
  },
  clearAll: () =>
    set({
      selectedStats: DEFAULT_METRICS,
      selectedPositions: [DEFAULT_SCOUT_POSITION],
      selectedBiometrics: [],
      showFavourites: false,
      activeTemplateId: null,
      templateName: '',
      toast: { title: '', message: '', type: 'success' },
      showToast: false,
    }),
  loadTemplate: (template) => {
    const state = get();
    // Find the template in the store by id
    const found = (state.savedScoutTemplates || []).find(
      (s) => s.id === template.id,
    );
    if (!found) return; // Optionally handle not found

    // Check if positions are changing
    const positionsChanged =
      state.selectedPositions.length !== found.selectedPositions.length ||
      state.selectedPositions.some(
        (pos, idx) => pos !== found.selectedPositions[idx],
      );

    // If positions changed, reset to Bio category
    let categoryUpdate = {};
    if (positionsChanged) {
      categoryUpdate = {
        scoutSelectedCategoryName: BIO_CATEGORY.name,
        scoutSelectedSubCategoryName: BIO_CATEGORY.subcategories[0].name,
      };
    }

    set({
      selectedStats: found.selectedStats,
      selectedBiometrics: found.selectedBiometrics,
      selectedPositions: found.selectedPositions,
      showFavourites: found.showFavourites,
      templateName: template.name || '',
      activeTemplateId: template.id,
      loadModalOpen: false,
      ...categoryUpdate,
    });
  },
  deleteTemplate: async ({ id, remove, openLoadModalAfter }) => {
    const state = get();
    const updatedTemplates = state.savedScoutTemplates.filter(
      (t) => t.id !== id,
    );
    const removedTemplate = state.savedScoutTemplates.find((t) => t.id === id);
    set({
      savedScoutTemplates: updatedTemplates,
      loadModalOpen: openLoadModalAfter,
      ...(state.activeTemplateId === id && {
        activeTemplateId: null,
        templateName: '',
        selectedStats: DEFAULT_METRICS,
        selectedPositions: [DEFAULT_SCOUT_POSITION],
        selectedBiometrics: [],
        showFavourites: false,
      }),
    });
    if (typeof remove === 'function') {
      await remove(removedTemplate);
    }
  },
  saveCurrentSetup: async ({ user, add, id, openLoadModalAfter }) => {
    const state = get();
    const savedSetup = state.savedScoutTemplates?.find((s) => s.id === id);
    const setup = {
      ...savedSetup,
      name: state.templateName,
      selectedStats: state.selectedStats,
      selectedPositions: state.selectedPositions,
      selectedBiometrics: state.selectedBiometrics,
      showFavourites: state.showFavourites,
      lastEditor: user?.name,
      lastModified: Date.now(),
    };
    const otherSetups =
      state.savedScoutTemplates?.filter((s) => s.id !== id) || [];
    const updatedSetups = otherSetups.concat(setup);
    try {
      await add({ scoutSetups: updatedSetups });
      set({
        toast: {
          message: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.QUICK]?.message,
          title: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.QUICK]?.title,
          type: 'success',
        },
        showToast: true,
        savedScoutTemplates: updatedSetups,
        activeTemplateId: setup.id,
        templateName: setup.name,
        loadModalOpen: openLoadModalAfter,
      });
    } catch (error) {
      set({
        toast: {
          message: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.QUICK]?.message,
          title: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.QUICK]?.title,
          type: 'error',
        },
        showToast: true,
        loadModalOpen: openLoadModalAfter,
      });
    }
  },
  saveAsNewSetup: async ({ user, add, id, openLoadModalAfter }) => {
    const state = get();
    // // id: the target id of the setup to copy
    const copyThisSetup = state.savedScoutTemplates?.find((s) => s.id === id);
    // if using from a table a different template may be chosen to provide the values other than in current state
    const newSetup = {
      id: crypto.randomUUID(),
      name: state.templateName,
      selectedStats: copyThisSetup
        ? copyThisSetup.selectedStats
        : state.selectedStats,
      selectedPositions: copyThisSetup
        ? copyThisSetup.selectedPositions
        : state.selectedPositions,
      selectedBiometrics: copyThisSetup
        ? copyThisSetup.selectedBiometrics
        : state.selectedBiometrics,
      showFavourites: copyThisSetup
        ? copyThisSetup.showFavourites
        : state.showFavourites,
      lastEditor: user?.name,
      lastModified: Date.now(),
      creator: user.name,
      creatorId: user.sub,
      created: Date.now(),
    };
    // Handle empty or non-existent savedScoutTemplates
    const existingSetups = Array.isArray(state.savedScoutTemplates)
      ? state.savedScoutTemplates
      : [];
    const updatedSetups = [...existingSetups, newSetup];
    try {
      await add({ scoutSetups: updatedSetups });
      set({
        toast: {
          message: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.SAVE_AS]?.message,
          title: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.SAVE_AS]?.title,
          type: 'success',
        },
        showToast: true,
        savedScoutTemplates: updatedSetups,
        activeTemplateId: newSetup.id,
        templateName: newSetup.name,
        loadModalOpen: openLoadModalAfter,
      });
    } catch (error) {
      set({
        toast: {
          message: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.SAVE_AS]?.message,
          title: TOAST_MESSAGES[SCOUT_TEMPLATE_MODES.SAVE_AS]?.title,
          type: 'error',
        },
        showToast: true,
        loadModalOpen: openLoadModalAfter,
      });
    }
  },
});
